import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../atoms/button";
import BotOpsConfigDiff from "./botOpsConfigDiff";
import { formatSuggestCostUsd, getSuggestModels, suggestConfig } from "./functions";

export const SUGGEST_EXAMPLES = [
    {
        id: "tom",
        label: "Tom mais descontraído",
        text: "o tema ta muito formal, queria que o bot mudasse o tom pra mais descontraido",
    },
    {
        id: "handoff-facil",
        label: "Handoff fácil demais",
        text: "o handoff esta acontecendo muito facil, o bot passa pra humano em FAQ simples",
    },
    {
        id: "handoff-raro",
        label: "Handoff quase nunca",
        text: "o handoff nao esta acontecendo quase nunca, clientes pedem humano e o bot insiste",
    },
    {
        id: "preco",
        label: "Preço indevido",
        text: "o bot ta respondendo com informacao incorreta, dando preco quando nao é pra dar",
    },
    {
        id: "triagem",
        label: "Triagem leilão→loja",
        text: "o bot ta fazendo a triagem errada, muitos clientes de leilao sendo encaminhados pra fila da loja",
    },
];

const riskClass = {
    low: "bg-gray-700 text-gray-100",
    medium: "bg-amber-800 text-amber-50",
    high: "bg-red-800 text-red-100",
};

/** Limpa resultado/erro local para pedir outra análise (não desfaz Aceitar no editor). */
export function resetSuggestSessionState() {
    return {
        result: null,
        error: null,
        ignored: new Set(),
    };
}

function modelOptionLabel(m) {
    if (!m) return "";
    const short = m.label || (m.id || "").split("/").pop() || m.id;
    return m.provider ? `${short} (${m.provider})` : short;
}

export function suggestProgressLabel(status) {
    switch (status) {
        case "enqueue":
            return "Enfileirando análise…";
        case "queued":
            return "Na fila…";
        case "running":
            return "Gerando sugestões (pode levar ~1 min)…";
        case "retry":
            return "Reconectando ao status…";
        default:
            return "Analisando…";
    }
}

const BotOpsConfigSuggestPanel = ({
    token,
    onApplyPatch,
    acceptedIds = [],
}) => {
    const [problem, setProblem] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [ignored, setIgnored] = useState(() => new Set());
    const [catalog, setCatalog] = useState(null);
    const [selectedModel, setSelectedModel] = useState("");
    const [progress, setProgress] = useState(null);
    const runIdRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const data = await getSuggestModels(token);
            if (cancelled || data?.type === "error") return;
            setCatalog(data);
            setSelectedModel((prev) => prev || data.default_model || "");
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const modelOptions = useMemo(() => {
        const byId = new Map();
        for (const m of [...(catalog?.recommended || []), ...(catalog?.all || [])]) {
            if (m?.id && !byId.has(m.id)) byId.set(m.id, m);
        }
        if (catalog?.default_model && !byId.has(catalog.default_model)) {
            byId.set(catalog.default_model, {
                id: catalog.default_model,
                label: catalog.default_model.split("/").pop(),
            });
        }
        return [...byId.values()];
    }, [catalog]);

    const handleNovaAnalise = () => {
        runIdRef.current += 1; // invalida poll em andamento
        const next = resetSuggestSessionState();
        setResult(next.result);
        setError(next.error);
        setIgnored(next.ignored);
        setLoading(false);
        setProgress(null);
    };

    const handleSuggest = async () => {
        const runId = ++runIdRef.current;
        setLoading(true);
        setError(null);
        setResult(null);
        setIgnored(new Set());
        setProgress("enqueue");
        try {
            const data = await suggestConfig(token, {
                problem,
                model: selectedModel || undefined,
                onStatus: (s) => {
                    if (runIdRef.current === runId) setProgress(s);
                },
            });
            if (runIdRef.current !== runId) return;
            if (data?.type === "error") {
                setResult(null);
                setError(data.message || "Erro ao pedir sugestões");
                return;
            }
            setResult(data);
        } finally {
            if (runIdRef.current === runId) {
                setLoading(false);
                setProgress(null);
            }
        }
    };

    const acceptedCount = acceptedIds.length;
    const hasSession = Boolean(result || error);
    const costLabel = formatSuggestCostUsd(result?.usage?.cost_usd);
    const tokensLabel =
        result?.usage?.total_tokens != null
            ? `${result.usage.total_tokens} tokens`
            : null;

    return (
        <section className="rounded-lg border border-[#b47c06]/40 bg-gray-900/40 p-4 mb-4 space-y-3">
            <div>
                <h2 className="text-laranjinha font-medium">Sugestões de alteração</h2>
                <p className="text-xs text-gray-400 mt-1">
                    Descreva o problema. Usa modelos top-tier (padrão Opus 4.8) — mudanças de config
                    são sensíveis. Orçamento alvo ≤ US$ 0,50 por análise. Roda em segundo plano
                    (~1 min); você aceita no editor e só publica com Salvar.
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {SUGGEST_EXAMPLES.map((ex) => (
                    <button
                        key={ex.id}
                        type="button"
                        onClick={() => setProblem(ex.text)}
                        className="text-xs px-2 py-1 rounded border border-[#b47c06]/40 text-gray-300 hover:bg-gray-800"
                    >
                        {ex.label}
                    </button>
                ))}
            </div>

            <textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                rows={3}
                className="w-full text-black p-2 rounded"
                placeholder="Ex.: o handoff está acontecendo muito fácil…"
            />

            <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1 text-xs text-gray-300 min-w-[220px]">
                    <span className="text-gray-400">Modelo da análise</span>
                    <select
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        disabled={loading || modelOptions.length === 0}
                        className="text-black p-2 rounded text-sm"
                        data-testid="suggest-model-select"
                        aria-label="Modelo da análise"
                    >
                        {modelOptions.length === 0 ? (
                            <option value="">Carregando modelos…</option>
                        ) : (
                            modelOptions.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {modelOptionLabel(m)}
                                    {m.id === catalog?.default_model ? " — padrão" : ""}
                                </option>
                            ))
                        )}
                    </select>
                </label>
                <div className="text-xs text-gray-500 pb-2 space-y-0.5">
                    {catalog?.default_model ? (
                        <p className="font-mono">padrão: {catalog.default_model}</p>
                    ) : null}
                    {catalog?.max_cost_usd != null ? (
                        <p>orçamento alvo: ≤ ${Number(catalog.max_cost_usd).toFixed(2)} / análise</p>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <Button
                    click={handleSuggest}
                    disabled={loading || problem.trim().length < 20}
                    classes="bg-[#d59106] hover:bg-[#b47c06] px-3 py-1.5 font-medium text-black"
                >
                    {loading
                        ? suggestProgressLabel(progress)
                        : hasSession
                          ? "Pedir outra análise"
                          : "Analisar e sugerir"}
                </Button>
                {loading ? (
                    <Button
                        click={handleNovaAnalise}
                        classes="border border-gray-400 bg-gray-800 hover:bg-gray-700 px-3 py-1.5 font-medium text-gray-50"
                    >
                        Cancelar espera
                    </Button>
                ) : null}
                {hasSession && !loading ? (
                    <Button
                        click={handleNovaAnalise}
                        classes="border border-gray-400 bg-gray-800 hover:bg-gray-700 px-3 py-1.5 font-medium text-gray-50"
                    >
                        Nova análise
                    </Button>
                ) : null}
                {acceptedCount > 0 ? (
                    <span className="text-xs text-amber-200">
                        {acceptedCount} patch(es) aceito(s) no editor — lembre de Salvar.
                    </span>
                ) : null}
            </div>

            {loading && progress ? (
                <p className="text-xs text-gray-400" data-testid="suggest-progress">
                    {suggestProgressLabel(progress)}
                </p>
            ) : null}

            {error ? (
                <p className="text-sm text-red-300" role="alert">
                    {error}
                </p>
            ) : null}

            {result?.diagnosis ? (
                <div className="rounded-md border border-[#b47c06]/25 bg-black/30 p-3 text-sm text-gray-200 space-y-2">
                    <p>
                        <span className="text-laranjinha">Diagnóstico:</span> {result.diagnosis}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        {result.model ? (
                            <p>
                                Modelo: <span className="font-mono text-gray-300">{result.model}</span>
                            </p>
                        ) : null}
                        {costLabel ? (
                            <p data-testid="suggest-cost">
                                Custo: <span className="font-mono text-gray-200">{costLabel}</span>
                                {tokensLabel ? (
                                    <span className="text-gray-500"> · {tokensLabel}</span>
                                ) : null}
                            </p>
                        ) : null}
                    </div>
                    {Array.isArray(result.warnings) && result.warnings.length > 0 ? (
                        <ul className="text-xs text-amber-200 list-disc pl-4">
                            {result.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            ) : null}

            {Array.isArray(result?.patches)
                ? result.patches.map((patch) => {
                      const isAccepted = acceptedIds.includes(patch.id);
                      const isIgnored = ignored.has(patch.id);
                      if (isIgnored) return null;
                      return (
                          <article
                              key={patch.id}
                              className="rounded-lg border border-[#b47c06]/30 p-3 space-y-2 bg-gray-950/40"
                          >
                              <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="text-sm font-medium text-[#d59106]">
                                      {patch.title}
                                  </h3>
                                  <span className="text-xs text-gray-400 font-mono">
                                      {patch.target}
                                  </span>
                                  <span
                                      className={`text-xs px-2 py-0.5 rounded ${
                                          riskClass[patch.risk] || riskClass.medium
                                      }`}
                                  >
                                      risco {patch.risk}
                                  </span>
                                  {isAccepted ? (
                                      <span className="text-xs text-green-300">Aceito</span>
                                  ) : null}
                              </div>
                              <p className="text-xs text-gray-300">{patch.reason}</p>
                              <BotOpsConfigDiff before={patch.before} after={patch.after} />
                              {!isAccepted ? (
                                  <div className="flex flex-wrap gap-2">
                                      <Button
                                          click={() => onApplyPatch(patch)}
                                          classes="bg-green-700 hover:bg-green-600 px-3 py-1.5 font-medium text-white"
                                      >
                                          Aceitar
                                      </Button>
                                      <Button
                                          click={() =>
                                              setIgnored((prev) => new Set(prev).add(patch.id))
                                          }
                                          classes="border border-gray-400 bg-gray-800 hover:bg-gray-700 px-3 py-1.5 font-medium text-gray-50"
                                      >
                                          Ignorar
                                      </Button>
                                  </div>
                              ) : null}
                          </article>
                      );
                  })
                : null}
        </section>
    );
};

export default BotOpsConfigSuggestPanel;
